import { useState, useEffect } from "react"
import { Iconify } from "@/src/common/iconify"
import { useBoolean } from "@/src/hooks/use-boolean"
import { DashboardContent } from "@/src/layouts/dashboard"
import { useInternalStore, type InternalWorkspaceRow } from "@/src/store"
import { CreateWorkspaceModal } from "@/src/features/internalUsers/modals/CreateWorkspaceModal"
import { UsersSideView, USERS_SIDE_VIEW_WIDTH } from "@/src/features/internalUsers/components/UsersSideView"
import { WorkspaceInterviewSchedulingToggle } from "@/src/features/internalUsers/components/WorkspaceInterviewSchedulingToggle"
import { WorkspaceGenerateCsvColumnCatalogToggle } from "@/src/features/internalUsers/components/WorkspaceGenerateCsvColumnCatalogToggle"

import Box from "@mui/material/Box"
import Card from "@mui/material/Card"
import Stack from "@mui/material/Stack"
import Table from "@mui/material/Table"
import Avatar from "@mui/material/Avatar"
import Button from "@mui/material/Button"
import TableRow from "@mui/material/TableRow"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableHead from "@mui/material/TableHead"
import Typography from "@mui/material/Typography"
import TableContainer from "@mui/material/TableContainer"
import TablePagination from "@mui/material/TablePagination"
import CircularProgress from "@mui/material/CircularProgress"

// Re-export for consumers that imported from this file
export type WorkspaceRow = InternalWorkspaceRow

const getInitials = (name: string) =>
    name
        .split(" ")
        .map((word) => word[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()

// ----------------------------------------------------------------------

export default function Workspaces() {
    const { workspaces, loading, error, fetchWorkspaces, setSelectedWorkspaceId, pagination } = useInternalStore()
    const [selectedWorkspace, setSelectedWorkspace] = useState<InternalWorkspaceRow | null>(null)
    const [logoErrorIds, setLogoErrorIds] = useState<Set<string>>(new Set())
    const createModal = useBoolean()

    const handleLogoError = (workspaceId: string) => {
        setLogoErrorIds((previousLogoErrorIds) => new Set(previousLogoErrorIds).add(workspaceId))
    }

    const handleSelectWorkspace = (row: InternalWorkspaceRow | null) => {
        setSelectedWorkspace(row)
        setSelectedWorkspaceId(row?.id ?? "")
    }

    useEffect(() => {
        fetchWorkspaces({ page: 0, size: 20 })
    }, [fetchWorkspaces])

    useEffect(() => {
        setLogoErrorIds(new Set())
    }, [workspaces])

    const handleWorkspacesPageChange = (_paginationEvent: unknown, newPage: number) => {
        fetchWorkspaces({ page: newPage, size: pagination.pageSize })
    }

    const handleWorkspacesRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newPageSize = Number(event.target.value) || 20
        fetchWorkspaces({ page: 0, size: newPageSize })
    }

    return (
        <DashboardContent
            sx={{
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                paddingBottom: "20px",
                mt: 5,
                mr: selectedWorkspace ? `${USERS_SIDE_VIEW_WIDTH}px` : 0,
                transition: (theme) =>
                    theme.transitions.create("margin-right", { duration: theme.transitions.duration.enteringScreen }),
            }}
        >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="h4" fontWeight={700}>
                        Workspaces
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Manage recruiter workspaces and user access.
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<Iconify icon="mingcute:add-line" />}
                    onClick={createModal.onTrue}
                >
                    Create Workspace
                </Button>
            </Stack>

            <Card
                sx={{
                    overflow: "hidden",
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 2,
                }}
            >
                {error && (
                    <Box sx={{ p: 2, bgcolor: "error.lighter", color: "error.darker" }}>
                        <Typography variant="body2">{error}</Typography>
                    </Box>
                )}
                {loading && workspaces.length === 0 ? (
                    <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
                        <CircularProgress />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                            Loading workspaces...
                        </Typography>
                    </Stack>
                ) : (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: "grey.50" }}>
                                    <TableCell sx={{ fontWeight: 600, color: "text.secondary", py: 2 }}>
                                        Workspace
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: "text.secondary", py: 2 }}>
                                        Users
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: "text.secondary", py: 2 }}>
                                        Available Credits
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: "text.secondary", py: 2 }}>
                                        Status
                                    </TableCell>
                                    <TableCell
                                        align="center"
                                        sx={{ fontWeight: 600, color: "text.secondary", py: 2, whiteSpace: "nowrap" }}
                                    >
                                        CSV Headers
                                    </TableCell>
                                    <TableCell
                                        align="center"
                                        sx={{ fontWeight: 600, color: "text.secondary", py: 2, whiteSpace: "nowrap" }}
                                    >
                                        Interview Scheduling
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {workspaces.map((row) => {
                                    const isSelected = selectedWorkspace?.id === row.id
                                    return (
                                        <TableRow
                                            key={row.id}
                                            hover
                                            selected={isSelected}
                                            onClick={() => handleSelectWorkspace(row)}
                                            sx={{
                                                cursor: "pointer",
                                                "&:last-child td": { borderBottom: 0 },
                                                "& td": { py: 2, borderBottom: 1, borderColor: "divider" },
                                                ...(isSelected && {
                                                    bgcolor: "primary.lighter",
                                                    "& td:first-of-type": {
                                                        borderLeft: "3px solid",
                                                        borderLeftColor: "primary.main",
                                                    },
                                                }),
                                            }}
                                        >
                                            <TableCell>
                                                <Stack direction="row" alignItems="center" spacing={1.5}>
                                                    {row.logoUrl && !logoErrorIds.has(row.id) ? (
                                                        <Box
                                                            component="img"
                                                            src={row.logoUrl}
                                                            alt={row.name}
                                                            onError={() => handleLogoError(row.id)}
                                                            sx={{
                                                                width: 40,
                                                                height: 40,
                                                                borderRadius: 1,
                                                                objectFit: "cover",
                                                                bgcolor: "grey.200",
                                                            }}
                                                        />
                                                    ) : (
                                                        <Avatar
                                                            sx={{
                                                                width: 40,
                                                                height: 40,
                                                                bgcolor: "grey.300",
                                                                color: "grey.700",
                                                                fontWeight: 600,
                                                                fontSize: "0.875rem",
                                                            }}
                                                        >
                                                            {getInitials(row.name)}
                                                        </Avatar>
                                                    )}
                                                    <Stack
                                                        direction="row"
                                                        alignItems="center"
                                                        spacing={0.5}
                                                        onClick={(event) => event.stopPropagation()}
                                                    >
                                                        <Typography variant="body2" fontWeight={500}>
                                                            {row.name}
                                                        </Typography>
                                                        <Iconify
                                                            icon={
                                                                isSelected
                                                                    ? "eva:chevron-down-fill"
                                                                    : "eva:chevron-right-fill"
                                                            }
                                                            width={18}
                                                            sx={{ color: "text.disabled" }}
                                                        />
                                                    </Stack>
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Iconify
                                                        icon="mdi:people-outline"
                                                        width={20}
                                                        sx={{ color: "text.secondary" }}
                                                    />
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight={500}
                                                        sx={{ textDecoration: "underline", cursor: "pointer" }}
                                                        onClick={(event) => {
                                                            event.stopPropagation()
                                                            setSelectedWorkspace(row)
                                                        }}
                                                    >
                                                        {row.userCount}
                                                    </Typography>
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">
                                                    {row.videoCreditsTotal}V / {row.audioCreditsTotal}A /{" "}
                                                    {row.outboundAudioCreditsTotal}OA
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        display: "inline-block",
                                                        px: 1.5,
                                                        py: 0.5,
                                                        borderRadius: 10,
                                                        typography: "caption",
                                                        fontWeight: 600,
                                                        bgcolor:
                                                            row.status === "active" ? "success.lighter" : "grey.200",
                                                        color: "#fff",
                                                    }}
                                                >
                                                    {row.status.toUpperCase()}
                                                </Box>
                                            </TableCell>
                                            <TableCell align="center">
                                                <WorkspaceGenerateCsvColumnCatalogToggle
                                                    workspaceId={row.id}
                                                    enabled={row.generateCsvColumnCatalogEnabled}
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <WorkspaceInterviewSchedulingToggle
                                                    workspaceId={row.id}
                                                    enabled={row.interviewSchedulingEnabled}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    )
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                {!(loading && workspaces.length === 0) && (
                    <TablePagination
                        component="div"
                        count={pagination.totalElements}
                        page={pagination.page}
                        onPageChange={handleWorkspacesPageChange}
                        rowsPerPage={pagination.pageSize}
                        rowsPerPageOptions={[10, 20, 40, 100]}
                        onRowsPerPageChange={handleWorkspacesRowsPerPageChange}
                        sx={{ borderTop: 1, borderColor: "divider" }}
                    />
                )}
            </Card>

            {selectedWorkspace && (
                <UsersSideView
                    workspace={selectedWorkspace}
                    open={!!selectedWorkspace}
                    onClose={() => handleSelectWorkspace(null)}
                />
            )}

            <CreateWorkspaceModal
                open={createModal.value}
                onClose={createModal.onFalse}
                onSuccess={() => {
                    createModal.onFalse()
                    fetchWorkspaces({ page: 0, size: pagination.pageSize })
                }}
            />
        </DashboardContent>
    )
}
